/**
 * Landscape HUD (GDD §7.2; frame 01). Desktop: score and enemy abilities top left, LEVEL badge, section name and
 * progress top centre with the bridge-guns alert beneath, fuel top right, JETS bottom left, pause and settings
 * bottom right (D16). Touch landscape (Q6): pause moves top left beside the score, JETS goes under the fuel panel,
 * settings open from the pause menu, and the steering pad and fire button take the bottom corners; the enemy
 * abilities shrink to compact chips so they stay clear of the pad.
 */
import { add, el, jetIcon } from './dom.ts';
import {
  abilityGroup,
  alertChip,
  bannerPart,
  fuelPanel,
  iconButton,
  scorePanel,
  titleParts,
  touchControls,
  type HudRefs,
} from './parts.ts';

export function buildLandscape(doc: Document, touch: boolean): HudRefs {
  const panels: HTMLElement[] = [];

  // Top left: (touch: the pause button,) the score panel, enemy abilities beneath.
  const tl = el(doc, 'div', 'hw-tl');
  const menuBtn = touch ? add(tl, iconButton(doc, 'pause', 'Pause')) : null;
  const col = add(tl, el(doc, 'div', 'hw-tl-col'));
  const sp = scorePanel(doc);
  add(col, sp.box);
  // Touch screens: compact chips without the label, two rows at most, clear of the steering pad.
  const ab = abilityGroup(doc, !touch);
  add(col, ab.box);
  panels.push(tl);

  // Top centre (D16, D19).
  const tc = el(doc, 'div', 'hw-tc');
  const lv = add(tc, el(doc, 'div', 'hw-level hw-glass'));
  const row = add(lv, el(doc, 'div', 'hw-level-row'));
  const level = add(row, el(doc, 'span', 'hw-badge', 'LEVEL 01'));
  const section = add(row, el(doc, 'span', '', 'MISTY VALLEY'));
  const br = add(lv, el(doc, 'div', 'hw-bridge-row'));
  const bridgeFrom = add(br, el(doc, 'span', '', 'BRIDGE 01'));
  const track = add(br, el(doc, 'div', 'hw-track'));
  const progress = add(track, el(doc, 'div', 'hw-track-fill'));
  const bridgeTo = add(br, el(doc, 'span', '', 'BRIDGE 02'));
  const alert = add(tc, alertChip(doc));
  panels.push(tc);

  // Top right: fuel (touch: JETS beneath).
  const tr = el(doc, 'div', 'hw-tr');
  const fp = fuelPanel(doc);
  add(tr, fp.box);
  panels.push(tr);

  // JETS: bottom left, or under the fuel panel on touch screens.
  const jets = el(doc, 'div', 'hw-jets hw-glass');
  add(jets, el(doc, 'span', '', 'JETS'));
  const icons = add(jets, el(doc, 'div', 'hw-jet-icons'));
  const jetIcons = Array.from({ length: 5 }, () => icons.appendChild(jetIcon(doc)));
  if (touch) add(tr, jets);
  else {
    jets.classList.add('hw-bl');
    panels.push(jets);
  }

  // Bottom right on desktop: pause and settings (D16).
  let pauseBtn: HTMLButtonElement | null = null;
  let settingsBtn: HTMLButtonElement | null = null;
  if (!touch) {
    const brc = el(doc, 'div', 'hw-br');
    pauseBtn = add(brc, iconButton(doc, 'pause', 'Pause'));
    settingsBtn = add(brc, iconButton(doc, 'settings', 'Settings'));
    panels.push(brc);
  }

  const t = titleParts(doc);
  const bn = bannerPart(doc);
  panels.push(t.title, t.press, t.credit, bn.wrap);

  const controls: HTMLElement[] = [];
  let tc2: ReturnType<typeof touchControls> | null = null;
  if (touch) {
    tc2 = touchControls(doc);
    controls.push(tc2.pad, tc2.fire, tc2.autoFire);
  }

  return {
    panels,
    controls,
    score: sp.score,
    high: sp.high,
    fuelBox: fp.box,
    fuelPct: fp.pct,
    fuelFill: fp.fill,
    lowFuel: fp.low,
    jetIcons,
    jetsText: null,
    level,
    section,
    sectionYields: false,
    bridgeFrom,
    bridgeTo,
    progress,
    abilities: ab.box,
    abilityChips: ab.chips,
    alert,
    bannerWrap: bn.wrap,
    banner: bn.banner,
    bannerHead: bn.head,
    bannerSub: bn.sub,
    bannerRows: bn.rows,
    bannerFinal: bn.final,
    bannerHigh: bn.high,
    bannerNew: bn.fresh,
    title: t.title,
    press: t.press,
    credit: t.credit,
    menuBtn,
    pauseBtn,
    settingsBtn,
    pad: tc2?.pad ?? null,
    thumb: tc2?.thumb ?? null,
    fire: tc2?.fire ?? null,
    fireBtn: tc2?.fireBtn ?? null,
    autoFire: tc2?.autoFire ?? null,
  };
}
